import React, { createContext, FC, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useProductScene } from '../../hooks/useProductScene';
import { ProductSideImage } from '../../types';

export type ProductEnvironment = 'CUSTOMIZER' | 'COMPOSER' | 'VIEWER';

/** Data-only shape used for partial updates */
interface AIStudioProductData {
    /** Product ID for 3D viewer (numeric) */
    productId?: number;
    /** Variant ID to select in 3D view */
    variantId?: number;
    /** Side images from customizer */
    sideImages?: ProductSideImage[];
    /** Whether 3D mode is available */
    has3D: boolean;
    /** Design document ID to load and render on the 3D preview */
    docId?: string | null;
    /** Composition document ID to load and apply attribute selections on the 3D preview */
    compositionDocId?: string | null;
    /** Serialized Babylon scene JSON received from the customizer */
    model3D?: string | Blob | null;
    /** Root URL for external assets referenced by the serialized Babylon scene */
    model3DRootUrl?: string | null;
    /** The Zakeke environment type for this product */
    environment: ProductEnvironment;
}

export interface AIStudioProductContextValue extends AIStudioProductData {
    /** Update one or more product properties (merges with current values) */
    updateProduct: (updates: Partial<AIStudioProductData>) => void;
    /** Reset all product data back to defaults */
    resetProduct: () => void;
}

const noop = () => {};

const defaultProductData: AIStudioProductData = {
    has3D: false,
    environment: 'CUSTOMIZER'
};

const AIStudioProductContext = createContext<AIStudioProductContextValue | null>(null);

export interface AIStudioProductProviderProps {
    productId?: number;
    skipSceneLookup?: boolean;
    variantId?: number;
    sideImages?: ProductSideImage[];
    docId?: string | null;
    compositionDocId?: string | null;
    model3D?: string | Blob | null;
    model3DRootUrl?: string | null;
    children: ReactNode;
}

export const AIStudioProductProvider: FC<AIStudioProductProviderProps> = ({
    productId,
    skipSceneLookup = false,
    variantId,
    sideImages,
    docId,
    compositionDocId,
    model3D,
    model3DRootUrl,
    children
}) => {
    const hasProductScene = useProductScene(skipSceneLookup ? undefined : productId);
    const has3D = !!model3D || hasProductScene;

    const initialData: AIStudioProductData = useMemo(
        () => ({ productId, variantId, sideImages, has3D, docId, compositionDocId, model3D, model3DRootUrl, environment: 'CUSTOMIZER' }),
        [productId, variantId, sideImages, has3D, docId, compositionDocId, model3D, model3DRootUrl]
    );

    const [productData, setProductData] = useState<AIStudioProductData>(initialData);

    // Sync state when the provider props (defaults) change
    React.useEffect(() => {
        setProductData(initialData);
    }, [initialData]);

    const updateProduct = useCallback((updates: Partial<AIStudioProductData>) => setProductData((prev) => ({ ...prev, ...updates })), []);
    const resetProduct = useCallback(() => setProductData(defaultProductData), []);

    const value = useMemo<AIStudioProductContextValue>(
        () => ({
            ...productData,
            updateProduct,
            resetProduct
        }),
        [productData, updateProduct, resetProduct]
    );

    return <AIStudioProductContext.Provider value={value}>{children}</AIStudioProductContext.Provider>;
};

export function useAIStudioProduct(): AIStudioProductContextValue {
    const context = useContext(AIStudioProductContext);
    if (!context) {
        // Return default values when used outside provider (standalone mode)
        return { has3D: false, environment: 'CUSTOMIZER', updateProduct: noop, resetProduct: noop };
    }
    return context;
}
